/**
 * Copyright (c) 2010-2021 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Feature } from 'ol';
import { WFS as OlWFS } from 'ol/format';
import { ReadOptions } from 'ol/format/Feature';
import { Options } from 'ol/format/WFS';
import Geometry from 'ol/geom/Geometry';
import GML2 from 'ol/format/GML2';
import GML3 from 'ol/format/GML3';
import GML32 from 'ol/format/GML32';

/**
 * WFS parser
 *
 * Extends ol.format.WFS to be able to read nested
 * feature collections in GML32. Can be removed later,
 * when the problem is resolved in ol.
 *
 * https://github.com/openlayers/openlayers/issues/12389
 */
export class WFS extends OlWFS {
    private readonly gmlFormat__: GML2 | GML3 | GML32;

    constructor(opt_options?: Options) {
        super(opt_options);
        this.gmlFormat__ = this['gmlFormat_'];
    }

    readFeatures(source: Document | Element | object | string, opt_options?: ReadOptions): Feature<Geometry>[] {
        if (this.gmlFormat__ instanceof GML32) {
            return this.readFeaturesGML32(source, opt_options);
        } else {
            return super.readFeatures(source, opt_options);
        }
    }

    private readFeaturesGML32(
        source: Document | Element | object | string,
        opt_options?: ReadOptions
    ): Feature<Geometry>[] {
        let doc: Document = null;
        if (source instanceof Document) {
            doc = source;
        } else if (typeof source === 'string') {
            const domParser = new DOMParser();
            doc = domParser.parseFromString(source as string, 'application/xml');
        } else {
            console.warn('Source for GML3.2 not supported!');
            return [];
        }

        let features: Feature<Geometry>[];
        if (this.hasNestedFeatureCollectionsGML32(doc)) {
            features = this.readNestedFeatureCollectionsGML32(doc, opt_options);
        } else {
            features = super.readFeatures(doc.documentElement, opt_options);
        }

        return features instanceof Array ? features : [];
    }

    private readNestedFeatureCollectionsGML32(doc: Document, opt_options?: ReadOptions): Feature<Geometry>[] {
        let features: Feature<Geometry>[] = [];
        const children = doc.documentElement.children;
        let i = 0;
        while (i < children.length) {
            const child = children.item(i);
            if (
                child.localName === 'member' &&
                child.children.length &&
                child.children.item(0).localName === 'FeatureCollection'
            ) {
                const collection = child.children.item(0);
                if (collection.children?.length) {
                    features = features.concat(super.readFeatures(collection, opt_options));
                }
            }
            i++;
        }
        return features;
    }

    private hasNestedFeatureCollectionsGML32(doc: Document): boolean {
        const children = doc.documentElement.children;
        let member: Element = null;
        let i = 0;

        while (i < children.length) {
            const child = children.item(i);
            if (child.localName === 'member') {
                member = child;
                break;
            }
            i++;
        }

        return member?.children?.length && member.children.item(0).localName === 'FeatureCollection';
    }
}
